/**
 * The one way the desk hands a file to the browser.
 *
 * Every export used to carry its own copy of "make an object URL, click an
 * anchor, revoke", and they disagreed on the details that decide whether
 * the file arrives at all (tester report 2026-10-09: a `blob:` link that
 * "does not open on my laptop"):
 *
 * - **Revoke late.** Revoking the object URL synchronously after `click()`
 *   (or on the next tick) can cancel the download in Chrome / Edge — the
 *   downloads list then shows a failed item whose link is the dead `blob:`
 *   URL. The URL is revoked after `REVOKE_AFTER_MS` instead.
 * - **Always a real name with an extension.** A `blob:` URL saved with no
 *   `download` name (or a name with no extension) lands on Windows as a
 *   UUID that nothing opens. `ensureExtension` adds one from the type.
 * - **The anchor is in the document when clicked** (Firefox ignores a
 *   detached anchor's click in some versions).
 * - **An error body is never saved as the file.** `saveResponse` refuses a
 *   non-2xx response with the server's own sentence.
 *
 * A `blob:` URL lives only in the tab that made it: copying one out of the
 * address bar or the downloads list and opening it elsewhere can never work.
 * The fix is that the click saves a named file, which is what this does.
 */

export const REVOKE_AFTER_MS = 60_000;

const EXT_BY_TYPE: Readonly<Record<string, string>> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/zip': 'zip',
  'application/x-zip-compressed': 'zip',
  'application/json': 'json',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'text/html': 'html',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/svg+xml': 'svg',
  'message/rfc822': 'eml',
};

/** The extension a content type is saved with, or null when unknown. */
export function extensionForType(contentType: string | null | undefined): string | null {
  if (!contentType) return null;
  const bare = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  return EXT_BY_TYPE[bare] ?? null;
}

const HAS_EXTENSION = /\.[A-Za-z0-9]{1,5}$/;

/**
 * A name Windows, macOS and Linux will all save: no path separators, no
 * reserved characters, no control characters, no trailing dot or space.
 * Non-ASCII letters are kept (a Hindi title stays a Hindi title).
 */
export function sanitizeFilename(name: string | null | undefined, fallback = 'download'): string {
  const cleaned = (name ?? '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f<>:"/\\|?*]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/, '');
  return cleaned || fallback;
}

/** `name` with an extension: its own, else the one `contentType` implies. */
export function ensureExtension(name: string, contentType: string | null | undefined): string {
  if (HAS_EXTENSION.test(name)) return name;
  const ext = extensionForType(contentType);
  return ext ? `${name}.${ext}` : name;
}

/** The file name a `Content-Disposition` header names, preferring the UTF-8 form. */
export function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || fallback;
}

/** The name a blob is saved under: sanitised, with an extension. */
export function downloadName(filename: string, contentType?: string | null): string {
  return ensureExtension(sanitizeFilename(filename), contentType);
}

/**
 * Save `blob` as `filename`. Returns the name used. The extension is taken
 * from the name, else from the blob's type.
 */
export function saveBlob(blob: Blob, filename: string): string {
  const name = downloadName(filename, blob.type);
  if (typeof document === 'undefined') return name;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
  return name;
}

/** Save text as a file (CSV, Markdown, JSON …). */
export function saveText(text: string, filename: string, type = 'text/plain'): string {
  const withCharset = type.includes('charset') ? type : `${type};charset=utf-8`;
  return saveBlob(new Blob([text], { type: withCharset }), filename);
}

/** A download the server refused, carrying its sentence and body. */
export class DownloadRefused extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, detail: unknown) {
    const obj =
      detail && typeof detail === 'object' && !Array.isArray(detail)
        ? (detail as Record<string, unknown>)
        : null;
    const message =
      typeof detail === 'string' && detail.trim()
        ? detail
        : typeof obj?.['message'] === 'string'
          ? (obj['message'] as string)
          : `The download failed (HTTP ${status}).`;
    super(message);
    this.name = 'DownloadRefused';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Save a fetch `Response` as a file, named from its `Content-Disposition`
 * (else `fallbackName`), with an extension from its `Content-Type`. A
 * non-2xx response throws `DownloadRefused` and saves nothing.
 */
export async function saveResponse(res: Response, fallbackName: string): Promise<string> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { detail?: unknown } | null;
    throw new DownloadRefused(res.status, body?.detail ?? null);
  }
  const contentType = res.headers.get('content-type');
  const named = filenameFromDisposition(res.headers.get('content-disposition'), fallbackName);
  const blob = await res.blob();
  const typed = blob.type || !contentType ? blob : new Blob([blob], { type: contentType });
  return saveBlob(typed, ensureExtension(named, contentType));
}
